'use client';

import React, { useEffect, useRef } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import CtaFinal from '../homepage/components/CtaFinal';

function useReveal(ref: React.RefObject<HTMLElement | null>) {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('in');
            observer.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    const els = ref?.current?.querySelectorAll('.rev');
    els?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, [ref]);
}

const methodCards = [
  {
    tag: '01 · Voice first',
    title: 'Cold calling is the job',
    body: 'Every CommandVA engagement is built around the phone. Your reps dial verified numbers inside proven calling windows, handle objections live, and book the meeting in the conversation. Email and LinkedIn exist to warm the call and follow it up, not the other way around.',
  },
  {
    tag: '02 · Targeting',
    title: 'Signal-scored accounts',
    body: 'We monitor funding events, hiring surges, leadership changes, and technology shifts across your market, score accounts weekly, and prioritize the ones showing buying intent now. Small verified lists beat big scraped ones, on the phone and in the inbox.',
  },
  {
    tag: '03 · Supporting touches',
    title: 'Email and LinkedIn, done carefully',
    body: "Supporting sequences run on professionally managed sending practices that protect your company's reputation with inbox providers. Volume stays deliberate, messaging stays specific, and every reply is handled by your rep, not a template.",
  },
  {
    tag: '04 · Qualification',
    title: 'The meeting SLA',
    body: 'Before launch we write the definition of a qualified meeting into your agreement: ICP fit, seniority bar, confirmed need, held with your team. Each qualified meeting carries a fee set in that same agreement, so we are paid for meetings your closers thank us for, and nothing else.',
  },
  {
    tag: '05 · Reporting',
    title: 'The weekly readout',
    body: 'Every week you get the full funnel: dials, live conversations, replies, meetings booked, meetings held, and the rate your closers accept them as qualified. If a number is off, the report says so and names the fix. What gets reported gets trusted.',
  },
  {
    tag: '06 · The reps',
    title: 'Career SDRs, screened in person',
    body: 'Every candidate is screened face to face, tested for sales aptitude and English fluency, and pitch-trained on your product before day one. Your reps work your US hours, full time, for you only. This is their career, not a stepping stone, which is why they stay.',
  },
];

const honesty = [
  { n: '1', q: 'Where is pricing published?', a: 'Ours is on the pricing page. Most hide it behind a quote form.' },
  { n: '2', q: 'Define a qualified meeting in writing.', a: 'Ours is in the contract, with the meeting fee beside it.' },
  { n: '3', q: 'What are the contract terms?', a: 'Month to month, 30 days notice. Lock-in prices in churn risk.' },
  { n: '4', q: 'Show a sample weekly report.', a: 'Full funnel from dials to accepted meetings. We share one on the first call.' },
  { n: '5', q: 'Do reps actually call?', a: 'Ours do, first. Many vendors quietly run email-only and call it outbound.' },
  { n: '6', q: 'Is my rep dedicated or pooled?', a: 'Dedicated, named, full time, your account only.' },
];

export default function HomepagePage() {
  const sec1 = useRef<HTMLDivElement>(null);
  const sec2 = useRef<HTMLDivElement>(null);
  const sec3 = useRef<HTMLDivElement>(null);
  useReveal(sec1);
  useReveal(sec2);
  useReveal(sec3);

  return (
    <>
      <Header />
      <main>
        {/* Hero */}
        <header
          className="py-24"
          style={{
            background:
              'radial-gradient(900px 500px at 85% -10%, rgba(201,137,65,0.15), transparent 60%), var(--navy-deep)',
          }}
        >
          <div className="max-w-site mx-auto px-7">
            <div className="inline-flex font-mono text-[12px] tracking-[0.18em] uppercase text-primary border border-primary/50 px-4 py-2 rounded-full mb-6">
              How we work
            </div>
            <h1 className="heading-1 text-white">
              The system behind<br />the reps
            </h1>
            <p className="mt-5 text-[19px] text-muted-cream leading-relaxed max-w-[62ch]">
              Clear meeting definitions. Clean, verified data. Precision outreach, not volume. Reporting with nothing to hide. Every piece of our process exists for one reason: meetings that actually turn into pipeline.
            </p>
          </div>
        </header>

        {/* Method cards */}
        <section ref={sec1} className="py-24">
          <div className="max-w-site mx-auto px-7">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {methodCards.map((card, i) => (
                <div
                  key={card.tag}
                  className="rev bg-white border border-line-color rounded-brand p-7"
                  style={{ transitionDelay: `${i * 0.07}s` }}
                >
                  <span className="block font-mono text-[11px] tracking-[0.18em] text-primary uppercase mb-3.5">
                    {card.tag}
                  </span>
                  <h3 className="heading-3 text-ink mb-3">{card.title}</h3>
                  <p className="text-[15px] text-muted-text leading-relaxed">{card.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Vendor honesty test */}
        <section ref={sec2} className="py-16 bg-navy">
          <div className="max-w-site mx-auto px-7">
            <div className="rev mb-13 max-w-[780px]">
              <div className="eyebrow">Vendor honesty test</div>
              <h2 className="heading-2 text-white">Ask any SDR provider these six questions</h2>
              <p className="mt-4 text-[19px] text-muted-cream leading-relaxed">
                Including us. A vendor who deflects on any of them is hiding something material.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {honesty.map((item, i) => (
                <div
                  key={item.n}
                  className="rev bg-navy-panel border border-line-navy rounded-brand p-6"
                  style={{ transitionDelay: `${i * 0.07}s` }}
                >
                  <b className="font-display text-[44px] text-primary block leading-none font-bold mb-2.5">
                    {item.n}
                  </b>
                  <span className="text-[13.5px] text-muted-cream block leading-relaxed">
                    <b className="block text-[16px] text-white font-semibold mb-1" style={{ fontFamily: 'var(--font-sans)' }}>
                      {item.q}
                    </b>
                    {item.a}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* We also say no */}
        <section ref={sec3} className="py-16">
          <div className="max-w-site mx-auto px-7">
            <div className="rev gold-strip">
              <div>
                <b className="text-primary">We also say no.</b> If your closers are not winning the meetings you already get, or your ICP is undefined, more top-of-funnel will not fix it. We decline engagements we cannot make successful, and we will tell you on the first call.
              </div>
            </div>
          </div>
        </section>

        <CtaFinal
          headline="Put the method to work"
          body="A 30-minute call covers your ICP, your meeting definition, and a launch date."
          cta="Book a strategy call"
        />
      </main>
      <Footer />
    </>
  );
}